import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import {
  LoanHelper,
  MessageService,
  RouteHelper,
  parseNumber,
  SessionStorageKey,
  SessionStorageState,
  SsoAccountFacade,
  AgreementSigningStatus,
} from "common";
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import { LoanSigningInfo } from "../../loan/domain/models/loan.model";

interface SignDetails {
  signAgreementNeeded: boolean;
  hasRenewalToSign: boolean;
  currentLoan: LoanSigningInfo
}

abstract class SignAgreementNeededGuard {
  constructor(
    protected router: Router,
    private loanFacade: LoanFacade,
    private messageService: MessageService,
    private ssoAccountFacade: SsoAccountFacade
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const accountId = parseNumber(RouteHelper.getParamValueByName(route, "accountId"));
    return forkJoin({
      currentLoan: this.loanFacade.getSigningInfo$(accountId).pipe(take(1)),
      currentSsoAccount: this.ssoAccountFacade.getCurrentSsoAccount$().pipe(take(1)),
    }).pipe(
      switchMap((res) => {
        return forkJoin({
          signAgreementNeeded: of(
            LoanHelper.getLoanAgreementState(res.currentLoan?.id, res.currentSsoAccount)?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser
          ),
          hasRenewalToSign: of(
            LoanHelper.getLoanWithAgreementState(res.currentLoan?.id, res.currentSsoAccount)?.renewalApplication?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser
          ),
          currentLoan: of(res.currentLoan)
        });
      }),
      catchError((error) => {
        this.messageService.error(error);
        SessionStorageState.set(SessionStorageKey.AccountSetupViewed, true);
        this.router.navigate(["home"]);
        return of([]);
      }),
      map((details: SignDetails) => this.handleSignNeeded(details))
    );
  }

  abstract handleSignNeeded(details: SignDetails);
}

@Injectable({
  providedIn: "root",
})
export class AgreementGuard extends SignAgreementNeededGuard {
  constructor(
    router: Router,
    loanFacade: LoanFacade,
    messageService: MessageService,
    ssoAccountFacade: SsoAccountFacade
  ) {
    super(router, loanFacade, messageService, ssoAccountFacade);
  }

  handleSignNeeded(details: SignDetails) {
    if (
      details?.signAgreementNeeded ||
      details?.hasRenewalToSign
    )
      return true;
    else if (
      !(details?.signAgreementNeeded || details?.hasRenewalToSign)
    ) {
      this.router.navigate(["dashboard"]);
      return false;
    } else {
      this.router.navigate([`account/${details.currentLoan.id}/renewal/confirmation`]);
      return false;
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class RenewalConfirmationGuard extends SignAgreementNeededGuard {
  constructor(
    router: Router,
    loanFacade: LoanFacade,
    messageService: MessageService,
    ssoAccountFacade: SsoAccountFacade
  ) {
    super(router, loanFacade, messageService, ssoAccountFacade);
  }

  handleSignNeeded(details: SignDetails): boolean {
    if (details?.signAgreementNeeded) {
      this.router.navigate([`account/${details.currentLoan.id}/setup/sign-agreement`]);
      return false;
    } else if (!details?.hasRenewalToSign) {
      this.router.navigate(["dashboard"]);
      return false;
    } else return true;
  }
}
